import Icon from "../components/Icon";
import { PageTitle, Card, Btn, Input, Spinner, Pagination } from "../components";
import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../api";

const PER_PAGE = 20;
const DETAIL_LABELS = {
  justificativa: "Justificativa",
  anexo_id: "Comprovante",
  tamanho: "Tamanho (bytes)",
  sha256: "Integridade do arquivo",
  categoria: "Categoria",
  descricao: "Descrição",
  data_pagamento: "Data de pagamento",
  valor: "Valor (R$)",
  km: "KM atual",
  km_anterior: "KM anterior",
  data_limite: "Data limite",
  km_limite: "KM limite",
  usuario_role: "Perfil do responsável",
  archive_id: "Arquivo de auditoria",
  registro: "Registro",
  nome: "Nome",
  cnpj: "CNPJ",
  telefone: "Telefone",
  email: "E-mail",
  endereco: "Endereço",
  rua: "Rua",
  numero: "Número",
  bairro: "Bairro",
  cidade: "Cidade",
  regiao: "Região",
  veiculo: "Veículo",
  placa: "Placa",
  data: "Data da rota",
  horario: "Horário",
  trecho: "Trecho",
  id_pedido: "N° Programação",
  motorista_nome: "Motorista",
  total_grupos: "Total de grupos",
  total_funcionarios: "Total de passageiros",
  total_passageiros: "Total de passageiros",
  regioes: "Regiões",
  motoristas: "Motoristas",
  veiculos: "Veículos",
  status: "Status",
  campos_editados: "Campos editados",
  senha_alterada: "Senha",
};

const HIDDEN_DETAIL_KEYS = new Set(["alteracoes", "campos_editados"]);

export default function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [acoes, setAcoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroAcao, setFiltroAcao] = useState("");
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const skipSearchDebounceRef = useRef(true);
  const [page, setPage] = useState(1);
  const [loadError, setLoadError] = useState("");

  const loadLogs = async (targetPage = page, query = searchQuery, acao = filtroAcao) => {
    setLoading(true);
    setLoadError("");
    try {
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(PER_PAGE),
      });
      if (query.trim()) params.set("q", query.trim());
      if (acao) params.set("acao", acao);
      const data = await apiFetch(`/logs?${params.toString()}`);
      if (Array.isArray(data)) {
        setLogs(data);
        setTotalLogs(data.length);
        setAcoes([...new Set(data.map((log) => log.acao).filter(Boolean))]);
      } else {
        setLogs(Array.isArray(data?.items) ? data.items : []);
        setTotalLogs(Number(data?.total) || 0);
        if (Array.isArray(data?.acoes) && data.acoes.length) {
          setAcoes(data.acoes);
        }
      }
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchDebounceRef.current) {
      skipSearchDebounceRef.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearchQuery(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadLogs(page, searchQuery, filtroAcao);
  }, [page, searchQuery, filtroAcao]);

  const formatDate = (d) => new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const detailLabel = (key) => DETAIL_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const formatDetailValue = (key, value) => {
    if (value == null || value === "") return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") {
      if (key === "senha_alterada") return value ? "Alterada" : "Não alterada";
      return value ? "Sim" : "Não";
    }
    if (key === "trecho") {
      if (value === "ida") return "Ida";
      if (value === "volta") return "Volta";
    }
    return String(value);
  };

  const renderDetalhes = (detalhes) => {
    if (!detalhes) return null;
    let parsed = detalhes;
    if (typeof detalhes === "string") {
      try { parsed = JSON.parse(detalhes); } catch { return <span>{detalhes}</span>; }
    }
    if (typeof parsed !== "object" || Array.isArray(parsed)) return <span>{String(detalhes)}</span>;

    const entries = Object.entries(parsed).filter(([key]) => !HIDDEN_DETAIL_KEYS.has(key));
    if (!entries.length) return null;

    return (
      <div style={{ display: "grid", gap: 4, marginTop: 8 }}>
        {entries.map(([key, value]) => (
          <div key={key} style={{ fontSize: '0.875rem', color: "var(--muted)" }}>
            <strong style={{ color: "var(--text)" }}>{detailLabel(key)}:</strong> {formatDetailValue(key, value)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <PageTitle icon="history">Histórico de alterações</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            {totalLogs} registro{totalLogs !== 1 ? "s" : ""}{searchQuery.trim() ? ` · busca: "${searchQuery.trim()}"` : ""}
          </p>
        </div>
        <Btn variant="secondary" disabled={loading} onClick={() => loadLogs(page, searchQuery, filtroAcao)}><Icon name="refresh" /> Atualizar</Btn>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Input
            label="Buscar no histórico" type="search" placeholder="Usuário, registro ou ação"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <select aria-label="Filtrar por ação"
            value={filtroAcao}
            onChange={(e) => { setFiltroAcao(e.target.value); setPage(1); }}
            style={{ padding: "10px 12px", background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: '1rem', minHeight: 44, maxWidth: "100%", minWidth: 0, flex: "1 1 200px" }}
          >
            <option value="">Todas as ações</option>
            {acoes.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </Card>

      {loading && <Card style={{ textAlign: "center", padding: 40 }}><Spinner /></Card>}
      {loadError && <Card style={{ padding: 20 }}><p role="alert" style={{ color: "#ef4444", marginBottom: 12 }}>{loadError}</p><Btn variant="secondary" onClick={() => loadLogs(page, searchQuery, filtroAcao)}>Tentar novamente</Btn></Card>}

      {!loading && !loadError && logs.length === 0 && (
        <Card style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>Nenhuma alteração encontrada para esta busca.</Card>
      )}

      {!loading && !loadError && logs.length > 0 && (
        <div style={{ display: "grid", gap: 10 }}>
          {logs.map(log => (
            <Card key={log.id} style={{ padding: 16, overflowWrap: "anywhere" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <span style={{ fontWeight: 700, color: "#f97316" }}>{log.acao}</span>
                  <span style={{ color: "var(--muted)", margin: "0 8px" }}>·</span>
                  <span style={{ color: "var(--text)" }}>{log.entidade} #{log.entidade_id}</span>
                </div>
                <span style={{ fontSize: '0.875rem', color: "var(--muted)" }}>{formatDate(log.criado_em)}</span>
              </div>
              <div style={{ fontSize: '0.9375rem', color: "var(--muted)", marginTop: 6 }}>
                Por <strong style={{ color: "var(--text)" }}>{log.usuario_nome || "Sistema"}</strong>
                {log.ip ? ` · IP ${log.ip}` : ""}
              </div>
              {renderDetalhes(log.detalhes)}
            </Card>
          ))}
          <Pagination page={page} total={totalLogs} perPage={PER_PAGE} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
